/**
 * Document construction and migration.
 *
 * `migrateDocument` is the read-side counterpart to the versioned schema: a record
 * written by an older build is repaired here rather than rejected. It is a pure
 * function on purpose — a migration you cannot unit test is a migration you find
 * out about from a learner.
 */

import type { Clef } from '../theory/staff';
import { SCHEMA_VERSION, freshMeta, type DocumentPayload, type StoredDocument } from './types';

export const UNTITLED = 'Untitled';

export function createDocument(
  feature: string,
  payload: DocumentPayload,
  title = UNTITLED,
  now = Date.now(),
): StoredDocument {
  return { ...freshMeta(now), title, feature, payload };
}

/**
 * A blank page.
 *
 * `autoKey` starts **true**: a new document has nothing written on it, so there
 * is no key to be wrong about, and the app should be watching what the learner
 * writes rather than silently asserting C major. Starting at a fixed key meant
 * every accidental a learner wrote was read against a signature they had never
 * chosen.
 *
 * A document that has been saved carries whatever the learner decided — see the
 * migration below, which distinguishes "chose C" from "never said".
 */
export function emptyPayload(clef: Clef = 'treble', keyId = 'C'): DocumentPayload {
  return { clef, keyId, autoKey: true, strokes: [], overrides: {} };
}

/**
 * Repair a stored record into the current shape.
 *
 * Tolerates a partially written or older record rather than throwing: losing one
 * field is recoverable, losing the whole document is not. Raw strokes are the one
 * thing never invented — if they are unreadable the document is reported as
 * damaged instead of being silently emptied.
 */
export function migrateDocument(raw: unknown): StoredDocument | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Partial<Omit<StoredDocument, 'payload'>> & {
    payload?: Partial<DocumentPayload>;
  };
  if (typeof record.id !== 'string' || !record.id) return null;

  const payload: Partial<DocumentPayload> = record.payload ?? {};
  if (payload.strokes !== undefined && !Array.isArray(payload.strokes)) return null;

  const now = Date.now();
  return {
    id: record.id,
    schemaVersion: SCHEMA_VERSION,
    revision: typeof record.revision === 'number' && record.revision > 0 ? record.revision : 1,
    createdAt: typeof record.createdAt === 'number' ? record.createdAt : now,
    updatedAt: typeof record.updatedAt === 'number' ? record.updatedAt : now,
    deletedAt: typeof record.deletedAt === 'number' ? record.deletedAt : null,
    title: typeof record.title === 'string' ? record.title : UNTITLED,
    feature: typeof record.feature === 'string' ? record.feature : 'build',
    payload: {
      clef: (payload.clef as Clef) ?? 'treble',
      keyId: typeof payload.keyId === 'string' ? payload.keyId : 'C',
      /*
       * `undefined` means the record predates the field, not that the learner
       * turned detection off. Those documents were written while detection was
       * the only behaviour available to them, so they reopen detecting.
       */
      autoKey: payload.autoKey === undefined ? true : payload.autoKey === true,
      strokes: payload.strokes ?? [],
      overrides:
        payload.overrides && typeof payload.overrides === 'object' ? payload.overrides : {},
    },
  };
}

/** A short, honest title from what is on the page. */
export function suggestTitle(chordNames: (string | null)[], fallback = UNTITLED): string {
  const named = chordNames.filter((n): n is string => Boolean(n));
  if (!named.length) return fallback;
  if (named.length === 1) return named[0];
  return `${named[0]} … ${named[named.length - 1]}`;
}
